/**
 * MeetingDebriefView - Auto-detects meeting type and renders appropriate view
 *
 * Exports three separate components for each meeting type:
 * - InternalMeetingDebriefView
 * - OtherMeetingDebriefView (customer operational meetings)
 * - SalesMeetingDebriefView
 *
 * Handles internal, other, and sales meeting formats from the AI summary.
 */

import React from 'react';
import { useTranslation } from '../../Helpers/translate';
import type {
	InternalMeetingDebrief,
	OtherMeetingDebrief,
	SalesMeetingDebrief
} from 'App/resources/Model/MeetingDebrief';
import { parseDebrief } from './MeetingDebriefCommon';
import type { SummaryCardProps } from './MeetingDebriefCommon';
import { InternalMeetingDebriefView } from './InternalMeetingDebriefView';
import { OtherMeetingDebriefView } from './OtherMeetingDebriefView';
import { SalesMeetingDebriefView } from './SalesMeetingDebriefView';

// Re-export individual view components for direct usage
export { InternalMeetingDebriefView } from './InternalMeetingDebriefView';
export { OtherMeetingDebriefView } from './OtherMeetingDebriefView';
export { SalesMeetingDebriefView } from './SalesMeetingDebriefView';

type MeetingDebrief = InternalMeetingDebrief | OtherMeetingDebrief | SalesMeetingDebrief;

interface MeetingDebriefViewProps {
	debrief: MeetingDebrief | string | null;
	forceEnglish?: boolean;
	hideTitle?: boolean;
	/** The appointment modal's ask action, at the summary card header's end; the timeline passes none. */
	summaryHeaderEnd?: SummaryCardProps['headerEnd'];
}

export default function MeetingDebriefView({
	debrief: rawDebrief,
	forceEnglish,
	hideTitle,
	summaryHeaderEnd
}: MeetingDebriefViewProps) {
	const { t } = useTranslation(undefined, { lng: forceEnglish ? 'en-US' : undefined });
	const debrief = parseDebrief<MeetingDebrief>(rawDebrief);

	if (!debrief) {
		return null;
	}

	if (!('meeting_type' in debrief)) {
		return null;
	}

	switch (debrief.meeting_type) {
		case 'internal':
			return (
				<InternalMeetingDebriefView
					debrief={debrief}
					t={t}
					hideTitle={hideTitle}
					summaryHeaderEnd={summaryHeaderEnd}
				/>
			);
		case 'other':
			return (
				<OtherMeetingDebriefView
					debrief={debrief}
					t={t}
					hideTitle={hideTitle}
					summaryHeaderEnd={summaryHeaderEnd}
				/>
			);
		case 'sales':
			return (
				<SalesMeetingDebriefView
					debrief={debrief}
					t={t}
					hideTitle={hideTitle}
					summaryHeaderEnd={summaryHeaderEnd}
				/>
			);
		default:
			return null;
	}
}
